import { forwardRef, type ReactElement, useState } from 'react';

import { Text, theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';
import { withStyles } from 'legacyStyles';

import Collapse from 'Components/Collapse';
import JsonldForFaq from 'Components/JsonldForFaq';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const styles = {
  root: {
    maxWidth: 'none',
    [legacyTheme.breakpoints.match('md')]: {
      maxWidth: legacyTheme.width.normal,
    },
  },
  collapse: {
    marginBottom: 32,
    width: '100%',
    border: 'none',
    borderBottom: `1px solid var(--border-color, rgba(166,166,166,0.3))`,
    '&:last-of-type': {
      borderBottom: `none`,
    },
  },
  half: {
    maxWidth: legacyTheme.width.half,
    margin: '0 auto',
    [legacyTheme.breakpoints.match('md')]: {
      maxWidth: 'none',
    },
  },
};

const StyledBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${mediaMinWidth('md')} {
    width: 50%;
  }

  ${mediaMinWidth('lg')} {
    width: 50%;
  }
`;

const FaqContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing['4x']};

  width: 100%;
  padding: ${theme.spacing['12x']} ${theme.spacing['8x']} ${theme.spacing['8x']};

  background-color: ${theme.colors.neutral[100]};

  ${mediaMinWidth('md')} {
    flex-direction: column;
    padding-top: ${theme.spacing['20x']};
    padding-right: ${theme.spacing['15x']};
    padding-left: ${theme.spacing['15x']};
  }

  ${mediaMinWidth('lg')} {
    flex-direction: row;
    padding-top: ${theme.spacing['30x']};
  }
`;

type QuestionType = {
  question: string | ReactElement;
  rawQuestion?: string;
  answer: string | ReactElement;
};

type FaqProps = {
  backgroundColor?: string;
  classes?: Record<string, string>;
  dataFrom?: string;
  hasBorder?: boolean;
  shouldGenerateJsonld?: boolean;
  questions: QuestionType[];
  variant?: 'light' | 'dark' | 'noir';
  title: string;
  dataTestId?: string;
  dataClick?: string;
  keepLineBreaks?: boolean;
  markupName?: string;
  className?: string;
};

const FaqComponent = forwardRef<HTMLElement, FaqProps>(
  ({
    backgroundColor = 'beige',
    classes,
    dataFrom = 'faq-block',
    shouldGenerateJsonld = false,
    questions,
    variant = 'light',
    title,
    dataTestId,
    dataClick,
    keepLineBreaks = false,
    markupName = 'button',
  }) => {
    const [openedIndex, setOpenedIndex] = useState<number | null>(null);
    const { isMobile } = useResponsiveVariant({ legacy: false });
    return (
      <FaqContainer data-click={dataClick} data-testid={dataTestId}>
        <StyledBox>
          <Text
            color={variant === 'dark' ? 'neutral.100' : 'neutral.900'}
            paddingBottom="6x"
            paragraph
            variant="h3"
          >
            {title}
          </Text>
        </StyledBox>
        <StyledBox>
          {questions.map((questionData, idx) => {
            const { question, answer } = questionData;
            return (
              <Collapse
                key={String(idx)}
                answer={answer}
                classes={{ root: classes?.collapse }}
                dataClick={`toggle-collapse-${idx + 1}`}
                dataFrom={dataFrom}
                isOpen={openedIndex === idx}
                keepLineBreaks={keepLineBreaks}
                markupName={markupName}
                onClick={() => setOpenedIndex(idx)}
                question={question}
                questionTypographyVariant={isMobile ? 'p2' : 'p1'}
                style={{
                  '--border-color':
                    backgroundColor === 'vert'
                      ? 'beige'
                      : variant === 'noir'
                      ? theme.colors.primary[400]
                      : '',
                  marginBottom: 64,
                  width: '100%',
                  border: 'none',
                  borderBottom: `1px solid var(--border-color, rgba(166,166,166,0.3))`,
                  '&:last-of-type': {
                    borderBottom: `none`,
                  },
                }}
                variant={variant}
              />
            );
          })}
          {shouldGenerateJsonld && <JsonldForFaq questions={questions} />}
        </StyledBox>
      </FaqContainer>
    );
  },
);

FaqComponent.displayName = 'FAQ';

export const Faq = withStyles(FaqComponent, styles);
